import { Shuffle as CbiOverrideIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { PropsWithChildren, Ref, forwardRef } from 'react';

import { environment } from '@/config';
import FakeErrorButton from '@components/FakeErrorButton';
import { rocketCacheBustingIdAtom } from '@hooks/useApp';
import ClearCachesSection from './ClearCachesSection';
import {
  useAllFeatureToggles,
  useFeatureToggle,
  useResetFeatureToggles,
  useSetFeatureToggle,
} from './FeatureTogglesContext';
import { FeatureToggles } from './featureToggles';

const FeatureTogglePanel = (_props: PropsWithChildren, ref: Ref<HTMLDivElement>) => {
  // Yo dawg I heard you like feature toggles
  const showClearCachesSection = useFeatureToggle('Developer Features');
  const showTriggerErrorsSection =
    useFeatureToggle('Developer Features') && ['local', 'dev'].includes(environment);
  const [rocketCacheBustingId, setRocketCacheBustingId] = useAtom(rocketCacheBustingIdAtom);
  const toggles = useAllFeatureToggles();
  const { flipToggle } = useSetFeatureToggle();
  const resetFeatureToggles = useResetFeatureToggles();

  const handleOverrideRocketCbi = () => {
    const newCbi = 'o_' + crypto.randomUUID().substring(0, 7);
    setRocketCacheBustingId(newCbi);
  };

  return (
    <Paper
      variant="elevation"
      sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      ref={ref}
      tabIndex={0}
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100%',
        maxWidth: '30%',
        zIndex: 1400,
        padding: '2rem 1.5rem 1rem 1.5rem',
        overflow: 'auto',
      }}
      elevation={6}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          height: "100%"
        }}>
        {showClearCachesSection && <ClearCachesSection sx={{ marginBottom: 2 }} />}
        <Box sx={{
          flexGrow: "1"
        }}>
          <Typography variant="h6">Toggles</Typography>
          <List>
            {Object.entries(toggles).map(([toggleName, toggleValue]) => (
              <ListItem key={toggleName} disableGutters>
                <Switch
                  checked={toggleValue}
                  onChange={() => flipToggle(toggleName as keyof FeatureToggles)}
                  sx={{ marginLeft: '-12px' }}
                />
                <ListItemText sx={{ marginLeft: 1 }}>{toggleName}</ListItemText>
              </ListItem>
            ))}
          </List>
          <Button variant="contained" onClick={resetFeatureToggles}>
            <span>Reset toggles</span>
          </Button>
        </Box>

        <Box
          sx={{
            flexGrow: "0",
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            gap: 1
          }}>
          {showTriggerErrorsSection && (
            <>
              <Typography variant="h6">Rocket integration</Typography>
              <Box sx={{
                display: "flex"
              }}>
                <Tooltip title="Cache Busting Id used as query string in js file src values. Default is frontend commit.">
                  <span>CBI = {rocketCacheBustingId}</span>
                </Tooltip>
                <Tooltip title="Override default CBI with random one to force next rocket load to fetch new data">
                  <IconButton
                    aria-label="override with random"
                    size="small"
                    color="secondary"
                    onClick={handleOverrideRocketCbi}
                  >
                    <CbiOverrideIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="h6">Break things</Typography>
              {['ApiError', 'SyntaxError', 'AgError'].map((technique) => (
                <Tooltip
                  key={technique}
                  title={`Click to trigger a ${technique} error on purpose. Tempting isn't it?`}
                  arrow
                >
                  <span>
                    <FakeErrorButton
                      technique={technique}
                      sx={{
                        width: '12em',
                        justifyContent: 'flex-start',
                      }}
                      startIcon={
                        <span role="img" aria-label="bomb">
                          💣
                        </span>
                      }
                    >
                      {technique}
                    </FakeErrorButton>
                  </span>
                </Tooltip>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default forwardRef(FeatureTogglePanel);
